import React, { FC } from 'react'
import { Stack, Typography } from '@mui/material'
import { TokenBalance } from '@plug-core/providers/BalanceProvider'
import { AnimationPlayer } from '@components/AnimationPlayer'
import Button from '@components/Button'
import successAnimation from '@assets/lottie/success.json'
import { useNavigate } from 'react-router-dom'
import { NFT } from '@plug-core/interfaces/common'
import SendDetails from '@modules/SendDetails'
import { Fee, SendMode } from '@plug-core/hooks/useSend'

import type { UTXO } from '@plug-core/interfaces/walletActions'
import useSendReview from '@plug-core/hooks/useSendReview'
import { useTranslation } from 'react-i18next'

interface Props {
  toAddress: string
  token?: TokenBalance
  amount: string
  nft?: NFT
  cancelAction: () => void
  onError: (error: string) => void
  xtcSendMode?: string
  utxos: UTXO[]
  fee?: Fee
  mode?: SendMode
  maxFeePerGas: number | undefined
  maxPriorityFeePerGas: number | undefined
  erc20TransferGas: number | undefined
  gasLimit: number | undefined
}

const SendReview: FC<Props> = ({
  toAddress,
  token,
  amount,
  cancelAction,
  nft,
  onError,
  xtcSendMode,
  utxos,
  fee,
  mode,
  maxFeePerGas,
  maxPriorityFeePerGas,
  erc20TransferGas,
  gasLimit,
}) => {
  const navigate = useNavigate()

  const { sendSuccess, sendInProgress, performPay, isFirstTimeSend, knownContact } = useSendReview({
    amount,
    token,
    nft,
    toAddress,
    fee,
    utxos,
    xtcSendMode,
    onError,
    goBack: () => navigate(-1),
    mode,
    maxFeePerGas,
    maxPriorityFeePerGas,
    erc20TransferGas,
    gasLimit,
  })

  const { t } = useTranslation()

  const action = mode === SendMode.Send ? t('send.title') : t('send.canisterTopup')

  const content = sendSuccess ? (
    <Stack>
      <Typography variant="h6" textAlign="center" mt={2}>
        {mode === SendMode.Send ? t('send.review.sendSuccess') : t('send.review.topupSuccess')}
      </Typography>
      <AnimationPlayer animationData={successAnimation} />
    </Stack>
  ) : (
    <Stack>
      <Typography variant="h6" textAlign="center" mt={2}>
        {t('send.review.title', { action })}
      </Typography>

      <SendDetails
        toAddress={toAddress}
        nft={nft}
        token={token}
        amount={amount}
        isFirstTimeSend={isFirstTimeSend && mode === SendMode.Send}
        knownContact={knownContact}
      />
      <Stack bgcolor={(theme) => theme.palette.background.mid} px={2} py={2} spacing={0.5}>
        <Button
          variant="rainbow"
          loading={sendInProgress}
          disabled={sendInProgress}
          onClick={performPay}
          fullWidth
        >
          {action}
        </Button>
        <Button onClick={cancelAction} disabled={sendInProgress} fullWidth>
          {t('common.cancel')}
        </Button>
      </Stack>
    </Stack>
  )

  return content
}

export default SendReview
